/* The Teleoperators workspace layout: the people who take over robots, and their work.
 *
 * Three things live here and nowhere below, because all three have the workspace — not a
 * screen — as their natural lifetime:
 *
 *  1. The **seat**. `_console` above says who is signed in; the supervisors door says what
 *     this identity may do on the floor. It is fetched once, its 403 is answered here, and
 *     the screens below receive a total `CurrentUser` through `SeatProvider`.
 *  2. The **event stream**. One `EventSource` opens while the person is here and closes
 *     when they leave. Because the layout stays mounted for every page below, walking from
 *     the help queue into a takeover and back does not reconnect.
 *  3. The **header nav and status**, published to the shell. A teleop vendor works other
 *     people's robots, so it is shown the help queue and nothing that reads a fleet.
 *
 * What each robot is watched for (supervision) belongs to the robot's deployment, so it
 * lives under `/p/<org>/<project>/deploy`, not here.
 */

import {
  SeatOrgKind,
  SeatRole,
} from "@sx/protocol/fleet/supervision";
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Outlet, createFileRoute } from "@tanstack/react-router";
import { LiveBadge } from "@sx/ui";
import { retainedQueryData } from "@sx/api-client";

import {
  registerWorkspaceHeader,
  type AccountBadge,
  type WorkspaceNavItem,
} from "../../../lib/nav";
import { usePrincipalId } from "../../../lib/browser-state";
import { started } from "../../../lib/prefetch";
import { loadedSession } from "../../../lib/session";
import { WorkspaceGate } from "../../../shell/WorkspaceGate";
import { LinkDownBanner } from "../../../workspaces/fleet/components/LinkDownBanner";
import { useEventStream } from "../../../workspaces/fleet/live";
import { SeatProvider, fleetSeatQuery } from "../../../workspaces/fleet/seat";
import { useLive } from "../../../workspaces/fleet/store";
import { SEAT_ROLE_LABEL } from "../../../workspaces/fleet/labels";
import type { OrgKind } from "../../../workspaces/fleet/types";

export const Route = createFileRoute("/_console/teleoperators")({
  // Runs beside `_console`'s portfolio loader, not behind it, so the seat is part of the
  // same wait rather than a phase after the shell has rendered.
  loader: async ({ context }) => {
    const { fleetSeatQuery: loaderFleetSeatQuery } = await import(
      "../../../workspaces/fleet/seat"
    );
    const principalId = loadedSession(context.queryClient)?.principal.id;
    if (principalId === undefined) return;
    await started(
      context.queryClient.ensureQueryData(loaderFleetSeatQuery(principalId)),
    );
  },
  component: TeleoperatorsLayout,
});

/** The seat's role, as the floor reads it: admin is the fleet's owner, a teleoperator is
 * cleared to drive. It is a fleet-door grant, so it rides in the account menu only while
 * this workspace is on screen — the platform identity above it never changes. */
const ROLE_BADGE: Record<SeatRole, AccountBadge["variant"]> = {
  [SeatRole.UNSPECIFIED]: "outline",
  [SeatRole.ADMIN]: "gold",
  [SeatRole.MEMBER]: "default",
  [SeatRole.TELEOPERATOR]: "success",
};

/** What this seat may reach. A teleop vendor operates other people's robots: it gets the
 * help queue and the takeover it leads to; the sessions and people pages read a fleet's
 * robots and seats, which are not theirs. */
function teleoperatorNavItems(orgKind: OrgKind): WorkspaceNavItem[] {
  const queue: WorkspaceNavItem = { to: "/teleoperators", label: "Help queue", icon: "inbox", end: true };
  return orgKind === SeatOrgKind.TELEOP_VENDOR
    ? [queue]
    : [
        queue,
        { to: "/teleoperators/sessions", label: "Sessions", icon: "history" },
        { to: "/teleoperators/people", label: "People", icon: "people" },
      ];
}

function TeleoperatorsLayout() {
  // The fleet door's own answer, kept off the shell's `["me"]` key: this is a grant
  // question about one workspace, not the identity question the console already settled.
  const seatQuery = useQuery(fleetSeatQuery(usePrincipalId()));
  // Through `retainedQueryData`, never `.data`: a seat restored from the last reload must
  // not outlive its own revocation, and react-query keeps the last success when a refetch
  // fails. A permanent refusal drops it and the gate answers 403; a transient one keeps
  // the operator on the screen they were supervising from.
  const seat = retainedQueryData(seatQuery);
  const connection = useLive((state) => state.connection);

  useEventStream(seat !== undefined);

  // Only the facts the header actually shows are dependencies: a refetch returning the
  // same org kind and role must not churn it, and `seat` is a fresh object every time.
  // Re-registration IS the update — the shell reads the header through
  // `useSyncExternalStore`, so publishing re-renders the header and nothing else, and the
  // layout does not subscribe to what it writes, so there is no loop back into this effect.
  const orgKind = seat?.orgKind;
  const role = seat?.role;
  useEffect(() => {
    if (orgKind === undefined) return;
    return registerWorkspaceHeader({
      workspace: "fleet-teleoperators",
      items: teleoperatorNavItems(orgKind),
      // `LiveConnection` is a subset of the badge's own vocabulary, so the stream's state
      // IS the badge's state — no translation table to drift.
      status: <LiveBadge state={connection} label={`Events ${connection}`} />,
      badges:
        role === undefined
          ? []
          : [{ label: SEAT_ROLE_LABEL[role], variant: ROLE_BADGE[role] }],
    });
  }, [connection, orgKind, role]);

  if (seat === undefined)
    return <WorkspaceGate workspace="fleet-teleoperators" query={seatQuery} />;

  return (
    <SeatProvider seat={seat}>
      <div className="flex h-full min-h-0 flex-col">
        <LinkDownBanner />
        <div className="min-h-0 flex-1">
          <Outlet />
        </div>
      </div>
    </SeatProvider>
  );
}
